import {
  ensureFileIndexDb,
  isFileIndexEligibleDocId,
} from '@blocksuite/affine-block-database';
import { Service } from '@toeverything/infra';

import type { DocCreateMiddleware, DocRecord } from '../../doc';
import type { DocsStore } from '../../doc/stores/docs';
import type { DocCreateOptions } from '../../doc/types';

type FileIndexDocLookup = Pick<DocsStore, 'getBlockSuiteDoc'>;

const FILE_INDEX_INIT_RETRY_DELAYS_MS = [50, 250, 1000, 5000] as const;
const pendingFileIndexInitRetries = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

function scheduleFileIndexDbInitRetry(
  docsStore: FileIndexDocLookup,
  docId: string,
  options: DocCreateOptions,
  attempt: number
): void {
  if (attempt >= FILE_INDEX_INIT_RETRY_DELAYS_MS.length) {
    return;
  }

  if (pendingFileIndexInitRetries.has(docId)) {
    return;
  }

  const timer = setTimeout(() => {
    pendingFileIndexInitRetries.delete(docId);
    ensureFileIndexDbForCreatedDoc(docsStore, docId, options, attempt + 1);
  }, FILE_INDEX_INIT_RETRY_DELAYS_MS[attempt]);

  pendingFileIndexInitRetries.set(docId, timer);
}

export function ensureFileIndexDbForCreatedDoc(
  docsStore: FileIndexDocLookup,
  docId: string,
  options: DocCreateOptions,
  attempt = 0
): void {
  if (options.skipInit === true) {
    const pending = pendingFileIndexInitRetries.get(docId);
    if (pending) {
      clearTimeout(pending);
      pendingFileIndexInitRetries.delete(docId);
    }
    return;
  }

  const store = docsStore.getBlockSuiteDoc(docId);
  if (!store?.root) {
    scheduleFileIndexDbInitRetry(docsStore, docId, options, attempt);
    return;
  }
  const pending = pendingFileIndexInitRetries.get(docId);
  if (pending) {
    clearTimeout(pending);
    pendingFileIndexInitRetries.delete(docId);
  }
  if (!isFileIndexEligibleDocId(store.workspace.id, store.id)) {
    return;
  }

  ensureFileIndexDb({
    workspaceId: store.workspace.id,
    docId: store.id,
    store,
    caller: 'system',
  });
}

export class FileIndexDocCreateMiddleware
  extends Service
  implements DocCreateMiddleware
{
  constructor(private readonly docsStore: DocsStore) {
    super();
  }

  afterCreate(doc: DocRecord, options: DocCreateOptions): void {
    ensureFileIndexDbForCreatedDoc(this.docsStore, doc.id, options);
  }
}
